/**
 * ActivityFormSheet - Sheet lateral com formulário de atividade
 * Abre da direita para esquerda com campos específicos por tipo
 * Suporta texto rico e anexos para tipo "note"
 * 
 * Versão: 2.0.0
 * Atualização: RichTextEditor + Upload de anexos para notas
 */
import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Phone, 
  Calendar, 
  Mail, 
  CheckSquare, 
  FileText,
  Loader2,
  Save,
  Paperclip,
  Upload,
  X,
  Image as ImageIcon,
  File
} from 'lucide-react';
import { useCrmDealActivities } from '@/hooks/useCrmDealActivities';
import { useActivityAttachments, formatFileSize } from '@/hooks/useActivityAttachments';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import type { QuickActivityType } from './QuickActivityBar';
import { CRM_ACTIVITY_CONFIG } from './activityConfig';

// Schema de validação do formulário
const activityFormSchema = z.object({
  title: z.string().min(1, 'Título é obrigatório').max(100, 'Título muito longo'),
  description: z.string().max(5000, 'Descrição muito longa').optional(),
  scheduled_at: z.string().optional(),
});

type ActivityFormData = z.infer<typeof activityFormSchema>;

// Interface para arquivo pendente
interface PendingFile {
  id: string;
  file: File;
  preview?: string;
}

// Props do componente
interface ActivityFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activityType: QuickActivityType | null;
  dealId: string;
  onSuccess?: () => void;
}

// Configuração visual por tipo de atividade agora vem de activityConfig.ts (fonte única)
const activityConfig = CRM_ACTIVITY_CONFIG;

/**
 * Sheet lateral com formulário específico para cada tipo de atividade
 */
export function ActivityFormSheet({
  open,
  onOpenChange,
  activityType,
  dealId,
  onSuccess
}: ActivityFormSheetProps) {
  // Estados locais
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [richDescription, setRichDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Hook de atividades
  // enabled:false → só usamos a mutation, não disparar SELECT * em crm_deal_activities
  const { createActivity } = useCrmDealActivities(dealId, { enabled: false });
  
  // Hook de anexos (usado após criar atividade)
  const { uploadFile } = useActivityAttachments(null);

  // Form
  const form = useForm<ActivityFormData>({
    resolver: zodResolver(activityFormSchema),
    defaultValues: {
      title: '',
      description: '',
      scheduled_at: '',
    },
  });

  // Reset form quando o tipo muda ou abre
  React.useEffect(() => {
    if (open) {
      form.reset({
        title: '',
        description: '',
        scheduled_at: '',
      });
      setRichDescription('');
      setPendingFiles([]);
    }
  }, [open, activityType, form]);

  // Se não tem tipo, não renderiza
  if (!activityType) return null;

  const config = activityConfig[activityType];
  const Icon = config.icon;

  // Handler para adicionar arquivos
  const handleFilesSelected = (files: FileList | null) => {
    if (!files) return;

    const newFiles: PendingFile[] = [];
    
    Array.from(files).forEach(file => {
      // Valida tamanho (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: Arquivo muito grande (máx 10MB)`);
        return;
      }

      const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Cria preview para imagens
      let preview: string | undefined;
      if (file.type.startsWith('image/')) {
        preview = URL.createObjectURL(file);
      }

      newFiles.push({ id, file, preview });
    });

    setPendingFiles(prev => [...prev, ...newFiles].slice(0, 10)); // Máximo 10 arquivos
  };

  // Handler para remover arquivo pendente
  const removePendingFile = (id: string) => {
    setPendingFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== id);
    });
  };

  // Handler de drag and drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFilesSelected(e.dataTransfer.files);
  };

  // Submit do formulário
  const onSubmit = async (data: ActivityFormData) => {
    setIsSubmitting(true);
    
    try {
      // Usa descrição rica para notas, simples para outros tipos
      const description = config.showRichText ? richDescription : data.description;

      // Cria a atividade
      const activity = await createActivity.mutateAsync({
        deal_id: dealId,
        activity_type: activityType as Exclude<QuickActivityType, 'scheduling' | 'contracts'>,
        title: data.title,
        description: description || null,
        // v2.0.1: Converte horário local para UTC antes de salvar
        scheduled_at: data.scheduled_at 
          ? new Date(data.scheduled_at).toISOString() 
          : null,
      });
      
      // Upload dos anexos (se houver)
      if (pendingFiles.length > 0 && activity?.id) {
        const uploadPromises = pendingFiles.map(pf => 
          uploadFile({ activityId: activity.id, file: pf.file }).catch(err => {
            console.error(`Erro ao enviar ${pf.file.name}:`, err);
            return null;
          })
        );
        
        await Promise.all(uploadPromises);
      }

      // Limpa previews
      pendingFiles.forEach(pf => {
        if (pf.preview) URL.revokeObjectURL(pf.preview);
      });
      
      toast.success(`${config.title.replace('Nova ', '').replace('Novo ', '')} registrada com sucesso`);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Erro ao salvar atividade');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className={cn(
          "p-0 flex flex-col",
          config.showRichText 
            ? "w-[480px] sm:w-[560px]" 
            : "w-[360px] sm:w-[420px]"
        )}
      >
        {/* Header */}
        <SheetHeader className="p-4 pb-3 border-b">
          <div className="flex items-center gap-3">
            <div className={cn('p-2 rounded-lg', config.bgClass)}>
              <Icon className={cn('h-5 w-5', config.color)} />
            </div>
            <div>
              <SheetTitle className="text-base">{config.title}</SheetTitle>
              <SheetDescription className="text-xs">
                Preencha os detalhes da atividade
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Conteúdo com scroll */}
        <ScrollArea className="flex-1">
          <form id="activity-form" onSubmit={form.handleSubmit(onSubmit)} className="p-4 space-y-4">
            {/* Campo Título */}
            <div className="space-y-2">
              <Label htmlFor="title" className="text-xs font-medium">
                Título <span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                placeholder={config.titlePlaceholder}
                className="h-9 text-sm"
                {...form.register('title')}
              />
              {form.formState.errors.title && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.title.message}
                </p>
              )}
            </div>

            {/* Campo Descrição - RichTextEditor para notas, Textarea para outros */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">
                Descrição
              </Label>
              
              {config.showRichText ? (
                <RichTextEditor
                  content={richDescription}
                  onChange={setRichDescription}
                  placeholder="Adicione detalhes sobre a atividade..."
                  minHeight="180px"
                />
              ) : (
                <Textarea
                  id="description"
                  placeholder="Adicione detalhes sobre a atividade..."
                  className="min-h-[100px] text-sm resize-none"
                  {...form.register('description')}
                />
              )}
            </div>

            {/* Campo Data/Hora (apenas para meeting e task) */}
            {config.showSchedule && (
              <div className="space-y-2">
                <Label htmlFor="scheduled_at" className="text-xs font-medium">
                  Data e Hora
                </Label>
                <Input
                  id="scheduled_at"
                  type="datetime-local"
                  className="h-9 text-sm"
                  {...form.register('scheduled_at')}
                />
              </div>
            )}

            {/* Área de Anexos (apenas para notas) */}
            {config.showAttachments && (
              <div className="space-y-2">
                <Label className="text-xs font-medium flex items-center gap-2">
                  <Paperclip className="h-3.5 w-3.5" />
                  Anexos
                  {pendingFiles.length > 0 && (
                    <span className="text-muted-foreground">
                      ({pendingFiles.length}/10)
                    </span>
                  )}
                </Label>
                
                {/* Zona de upload drag-and-drop */}
                <div 
                  className={cn(
                    "border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors",
                    "hover:border-primary/50 hover:bg-muted/30"
                  )}
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <Upload className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">
                    Arraste arquivos ou clique para adicionar
                  </p>
                  <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                    Máximo 10MB por arquivo
                  </p>
                </div>

                {/* Input de arquivo oculto */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                  className="hidden"
                  onChange={(e) => handleFilesSelected(e.target.files)}
                />
                
                {/* Lista de arquivos pendentes */}
                {pendingFiles.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {pendingFiles.map(pf => (
                      <div 
                        key={pf.id} 
                        className="relative group aspect-square rounded-md overflow-hidden ring-1 ring-border"
                      >
                        {pf.preview ? (
                          <img 
                            src={pf.preview} 
                            alt={pf.file.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-muted flex flex-col items-center justify-center p-1">
                            <File className="h-5 w-5 text-muted-foreground mb-0.5" />
                            <span className="text-[8px] text-muted-foreground text-center line-clamp-2">
                              {pf.file.name}
                            </span>
                          </div>
                        )}
                        
                        {/* Botão remover */}
                        <button
                          type="button"
                          onClick={() => removePendingFile(pf.id)}
                          className={cn(
                            "absolute top-0.5 right-0.5 h-4 w-4 rounded-full",
                            "bg-destructive text-destructive-foreground",
                            "opacity-0 group-hover:opacity-100 transition-opacity",
                            "flex items-center justify-center"
                          )}
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </form>
        </ScrollArea>

        {/* Footer com botão */}
        <div className="p-4 pt-3 border-t">
          <Button 
            type="submit"
            form="activity-form"
            className="w-full h-9 text-sm"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Salvar {config.title.replace('Nova ', '').replace('Novo ', '')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
